import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { CrownLogo } from "../components/CrownLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

function PasswordField({ label, value, onChange, testid, show, setShow, placeholder = "••••••••" }) {
  return (
    <div>
      <Label className="text-cream">{label}</Label>
      <div className="relative mt-1.5">
        <Input
          data-testid={testid} type={show ? "text" : "password"}
          className="border-charcoal-border bg-charcoal pr-10 text-cream focus-visible:ring-gold"
          value={value} onChange={onChange} placeholder={placeholder} required minLength={6}
        />
        <button type="button" onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold"
          data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password !== form.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        setLoginFailed(false);
        await login(form.email, form.password);
        toast.success("Welcome back to Carlocs.");
        navigate("/dashboard");
      } else if (mode === "register") {
        await register(form.name, form.email, form.password);
        toast.success("Welcome to Carlocs.");
        navigate("/dashboard");
      } else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
        if (data.dev_reset_url) {
          toast.message("Dev mode: reset link ready", { description: "Email sending is off — use the link shown below." });
        } else {
          toast.success("If that email exists, a reset link is on its way.");
        }
        setForm((f) => ({ ...f, devUrl: data.dev_reset_url }));
      }
    } catch (err) {
      if (mode === "login") setLoginFailed(true);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: ["Welcome back", "Sign in to view your scalp history."],
    register: ["Create your account", "Track your loc scalp health over time."],
    forgot: ["Reset your password", "Enter your email and we'll send you a reset link."],
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-charcoal px-6">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.03]" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl border border-charcoal-border bg-charcoal-surface p-8 md:p-10"
      >
        <div className="mb-8 flex justify-center"><CrownLogo /></div>
        <h1 className="mb-1 text-center font-heading text-3xl text-cream">{titles[mode][0]}</h1>
        <p className="mb-8 text-center text-sm text-muted-foreground">{titles[mode][1]}</p>

        {mode === "forgot" && sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <MailCheck className="mx-auto mb-4 h-10 w-10 text-gold" />
            <p className="text-sm text-muted-foreground">If an account exists for <span className="text-cream">{form.email}</span>, a reset link has been sent. Check your inbox.</p>
            {form.devUrl && (
              <a href={form.devUrl} className="mt-4 block break-all rounded-lg border border-gold/40 bg-charcoal p-3 text-xs text-gold hover:underline" data-testid="dev-reset-link">
                {form.devUrl}
              </a>
            )}
            <button className="mt-6 flex items-center justify-center gap-1 text-sm text-gold hover:underline mx-auto" onClick={() => { setMode("login"); setSent(false); }} data-testid="back-to-login">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label className="text-cream">Name</Label>
                  <Input
                    data-testid="auth-name-input"
                    className="mt-1.5 border-charcoal-border bg-charcoal text-cream focus-visible:ring-gold"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name" required
                  />
                </div>
              )}
              <div>
                <Label className="text-cream">Email</Label>
                <Input
                  data-testid="auth-email-input" type="email"
                  className="mt-1.5 border-charcoal-border bg-charcoal text-cream focus-visible:ring-gold"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com" required
                />
              </div>

              {mode !== "forgot" && (
                <PasswordField label="Password" testid="auth-password-input" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} show={showPw} setShow={setShowPw} />
              )}
              {mode === "register" && (
                <PasswordField label="Confirm password" testid="auth-confirm-input" value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })} show={showConfirm} setShow={setShowConfirm} />
              )}

              {mode === "login" && (
                <div className="flex justify-end">
                  <button type="button" className="text-xs text-gold hover:underline" onClick={() => { setMode("forgot"); setSent(false); }} data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit" disabled={loading}
                className="w-full rounded-full bg-gold py-6 text-base font-500 text-charcoal hover:bg-gold/90"
                data-testid="auth-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                  mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
              </Button>
            </form>

            {mode === "login" && loginFailed && (
              <div className="mt-5 rounded-xl border border-gold/30 bg-gold/5 p-4 text-center" data-testid="login-failed-signup-prompt">
                <p className="text-sm text-muted-foreground">Can't sign in? You may not have an account yet.</p>
                <Button
                  type="button"
                  className="mt-3 w-full rounded-full bg-gold py-5 text-sm font-500 text-charcoal hover:bg-gold/90"
                  onClick={() => { setMode("register"); setLoginFailed(false); setForm((f) => ({ ...f, password: "", confirm: "" })); }}
                  data-testid="create-account-instead-btn"
                >
                  Create an account instead
                </Button>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {mode === "forgot" ? (
                <button className="flex items-center justify-center gap-1 text-gold hover:underline mx-auto" onClick={() => setMode("login")} data-testid="auth-toggle-mode">
                  <ArrowLeft className="h-4 w-4" /> Back to sign in
                </button>
              ) : (
                <>
                  {mode === "login" ? "New to Carlocs? " : "Already have an account? "}
                  <button className="text-gold hover:underline" onClick={() => { setLoginFailed(false); setMode(mode === "login" ? "register" : "login"); }} data-testid="auth-toggle-mode">
                    {mode === "login" ? "Create an account" : "Sign in"}
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
